import { type FC, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useMutation } from "@apollo/client";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { useTranslation } from "@/shared/hooks";
import {
  CompletePaymentMutation,
  CreatePaymentMutation,
  CreateProviderPaymentMutation,
} from "@/entities/payments";
import type {
  CompletePaymentMutationType,
  CompletePaymentMutationVariablesType,
  CreatePaymentMutationType,
  CreatePaymentMutationVariablesType,
  CreateProviderPaymentMutationType,
  CreateProviderPaymentMutationVariablesType,
} from "@/types/graphql/site.generated";
import {
  approvePayPalOrder,
  createPayPalOrder,
  getPaypalLocale,
} from "./payPalOrder";

export interface PayPalCheckoutButtonProviderProps {
  name: string;
  clientId?: string | null;
}

export interface PayPalCheckoutButtonProps {
  /** providers активной цены (спека §5.1): кнопка рисуется только если
   * среди них есть PAYPAL с непустым clientId, тем самым публичным
   * client-id и грузится JS SDK. */
  providers: PayPalCheckoutButtonProviderProps[];
  currency: string;
  productId: string;
  priceToken: string;
  recurrent: boolean;
  /** Платёж подтверждён PayPal и захвачен на сервере: диалог переключается
   * на опрос статуса тем же paymentId, что и у Stripe/Paynet. */
  onApproved: (paymentId: string) => void;
}

export const PayPalCheckoutButton: FC<PayPalCheckoutButtonProps> = ({
  providers,
  currency,
  productId,
  priceToken,
  recurrent,
  onApproved,
}) => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const clientId = providers.find((p) => p.name === "PAYPAL")?.clientId;
  // Платёж заводится по клику на кнопку (createOrder), а не при открытии
  // диалога: id нужен и в createOrder (чтобы вызвать createProviderPayment),
  // и в onApprove (чтобы завершить именно этот платёж). Ref, а не state:
  // PayPal SDK вызывает оба колбэка напрямую, без промежуточного рендера.
  const paymentIdRef = useRef<string | null>(null);

  const [createPayment] = useMutation<
    CreatePaymentMutationType,
    CreatePaymentMutationVariablesType
  >(CreatePaymentMutation);
  const [createProviderPayment] = useMutation<
    CreateProviderPaymentMutationType,
    CreateProviderPaymentMutationVariablesType
  >(CreateProviderPaymentMutation);
  const [completePayment] = useMutation<
    CompletePaymentMutationType,
    CompletePaymentMutationVariablesType
  >(CompletePaymentMutation);

  const errorMessage = t("payment.errorTitle");

  const createOrder = createPayPalOrder({
    createPayment,
    createProviderPayment,
    productId,
    priceToken,
    recurrent,
    setPaymentId: (id) => {
      paymentIdRef.current = id;
    },
    errorMessage,
  });

  const onApprove = approvePayPalOrder({
    completePayment,
    getPaymentId: () => paymentIdRef.current,
    onApproved,
    setError,
    errorMessage,
  });

  const onError = () => setError(errorMessage);

  // PayPal не в списке способов этой цены (спека §5.1): ни скрипт, ни
  // кнопку не рисуем вовсе.
  if (!clientId) return null;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <PayPalScriptProvider
        options={{
          clientId,
          currency,
          intent: "capture",
          components: "buttons",
          locale: getPaypalLocale(i18n.language),
        }}
      >
        <PayPalButtons
          fundingSource="paypal"
          style={{ layout: "vertical", label: "pay" }}
          createOrder={createOrder}
          onApprove={onApprove}
          onError={onError}
        />
      </PayPalScriptProvider>
    </Box>
  );
};
